/**
 * Beta Configuration
 * Detects beta environment and provides beta-specific settings
 */

export const BETA_CONFIG = {
  // Beta domain
  BETA_DOMAIN: 'beta.subvention360.com',

  // Beta user limits
  BETA_USER_LIMIT: 150, // Maximum number of beta users allowed
  CLIENT_LIMIT: 10,
  BETA_DURATION_DAYS: 7,

  // Features
  SKIP_STRIPE: true,
  SHOW_BETA_BADGE: true,
  ENABLE_ALL_FEATURES: true,
} as const;

/**
 * Check if current environment is beta
 */
export function isBetaEnvironment(): boolean {
  if (typeof window === 'undefined') return false;

  const hostname = window.location.hostname;

  // Check for beta domain
  const isBetaDomain = hostname === BETA_CONFIG.BETA_DOMAIN ||
                       hostname === 'beta.subvention360.com' ||
                       hostname.includes('beta.subvention360');

  // Check for dev mode override via localStorage or URL param
  const devBetaModeValue = localStorage.getItem('DEV_BETA_MODE');
  const hasUrlBetaParam = new URLSearchParams(window.location.search).has('beta');
  const isDevBetaMode = devBetaModeValue === 'true' || hasUrlBetaParam;

  // Localhost with beta flag
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isLocalBeta = isLocalhost && isDevBetaMode;

  // Debug logging
  console.debug('🔍 Beta Environment Check:', {
    hostname,
    isBetaDomain,
    devBetaModeValue,
    hasUrlBetaParam,
    isDevBetaMode,
    isLocalhost,
    isLocalBeta,
    finalResult: isBetaDomain || isLocalBeta
  });

  return isBetaDomain || isLocalBeta;
}

/**
 * Get environment name
 */
export function getEnvironment(): 'production' | 'beta' | 'development' {
  if (typeof window === 'undefined') {
    return import.meta.env.MODE === 'production' ? 'production' : 'development';
  }

  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }

  if (isBetaEnvironment()) {
    return 'beta';
  }

  return 'production';
}

/**
 * Get beta-specific configuration
 */
export function getBetaConfig() {
  const isBeta = isBetaEnvironment();

  return {
    isBeta,
    clientLimit: isBeta ? BETA_CONFIG.CLIENT_LIMIT : Infinity,
    skipStripe: isBeta && BETA_CONFIG.SKIP_STRIPE,
    showBetaBadge: isBeta && BETA_CONFIG.SHOW_BETA_BADGE,
    betaDurationDays: BETA_CONFIG.BETA_DURATION_DAYS,
  };
}

/**
 * Log environment info (useful for debugging)
 */
export function logEnvironmentInfo() {
  const env = getEnvironment();
  const config = getBetaConfig();

  console.log('🌍 Environment:', env);
  console.log('⚙️ Config:', config);

  if (config.isBeta) {
    console.log('🧪 Beta mode active');
    console.log(`📊 Client limit: ${config.clientLimit}`);
    console.log(`⏱️ Duration: ${config.betaDurationDays} days`);
  }
}